import type { MbTextOverlay } from "./source-text-overlay";
import { SAFE_ZONE } from "./safe-zone";

/** 🆔 고지 오버레이 식별 접두 — 이 접두로 시작하는 `id` 만 고지로 취급한다. */
export const NOTICE_CAPTION_ID_PREFIX = "notice:";

/**
 * 🧭 고지 오버레이의 **출처** — 재정의 우선(QA 완료 조건)의 유일한 재료.
 *   · "one" = 그 컷에서 **개별로** 지정했다 → 이후 일괄 변경이 덮지 않는다.
 *   · "all" = **일괄 적용**이 깐 것 → 다음 일괄 변경이 자유롭게 덮는다.
 *   · "off" = 그 컷에서 **개별로 껐다**(빈 문구 표식 = 무덤) → 이후 일괄 적용이 되살리지 않는다.
 * ★별도 상태 필드를 만들지 않는 이유★: 출처를 오버레이 밖에 두면 오버레이가 살아 이사할 때(씬 분할·
 *   컷 재배열) 그 짝이 끊긴다. `id` 안에 적으면 오버레이와 한 몸으로 직렬화·이동한다.
 *
 * ★왜 "off" 무덤이 필요한가(적대 리뷰 P2)★: 개별 해제를 **키째 삭제**로 구현하면 그 컷은 "한 번도
 *   고지를 켠 적 없는 컷"과 데이터가 같아진다 — 다음 [모든 컷에 적용]이 그 컷을 **되살린다**.
 *   유저가 "이 컷만 빼자"고 판단한 결과가 조용히 뒤집히는 것이라, 빈 문구 표식을 남겨 그 판단을
 *   데이터에 박는다. 빈 문구는 **두 렌더러 모두 그리지 않는다**(remotion `TextOverlayLabelNode` 는
 *   `!text` 조기 반환 · 캔버스 `drawTextOverlays` 는 `if (!text) continue`) — 화면 영향 0.
 */
export type NoticeCaptionSource = "one" | "all" | "off";

export const NOTICE_CAPTION_ID: Readonly<Record<NoticeCaptionSource, string>> = {
  one: `${NOTICE_CAPTION_ID_PREFIX}one`,
  all: `${NOTICE_CAPTION_ID_PREFIX}all`,
  off: `${NOTICE_CAPTION_ID_PREFIX}off`,
};

/** 개별 결정(일괄이 덮지 않는 출처) — "one"(개별 문구) · "off"(개별 해제). */
export function isNoticeCaptionOverridden(source: NoticeCaptionSource | null): boolean {
  return source === "one" || source === "off";
}

/** 📝 프리셋 3종(승인 MVP) — 자유 입력과 병행한다(테넌트별 법적 프리셋 관리는 후속 분리). */
export const NOTICE_CAPTION_PRESETS: readonly string[] = [
  "연출된 이미지입니다",
  "이해를 돕기 위한 예시 이미지입니다",
  "AI로 생성된 이미지입니다",
];

/** 🖼️ 스토리 좌표계 세로 기준(360×640) — story-template `STORY_FRAME_H` 의 미러(그쪽은 비공개). */
export const NOTICE_CAPTION_FRAME_H = 640;
/** 🖼️ 스토리 좌표계 가로 기준(360×640). */
export const NOTICE_CAPTION_FRAME_W = 360;

/**
 * 📐 **고지 규격 SSOT** — 자막과 혼동되지 않는 **고정** 시각 체계.
 *
 * ★왜 유저가 못 고치게 고정하나★: 고지는 "이 화면이 연출임"을 알리는 **법적·윤리적 표시**다. 컷마다
 *   크기·위치가 다르면 그 자체로 신뢰를 깎고, 자막 크기로 키우면 자막과 구분이 안 된다. 자유 배치가
 *   필요한 강조문구는 이미 별도 창구(재편집 스튜디오 TextOverlayEditor)가 있다.
 *
 * ★`y` 는 **저장 규격**일 뿐 그리는 자리가 아니다(세이프존 3-b · 회장님 2026-09-24 «자막 바로 위 안쪽»)★
 *   `y = 0.945`(중심 604.8px)는 쇼츠 아래 UI 띠(88.54%~ · `safe-zone.ts`) 안이라 버튼·설명글에 가렸다.
 *   이 값은 이미 작업물마다 오버레이 사본으로 **저장돼 있어** 상수를 바꿔도 안 움직인다. 그래서 저장값은
 *   그대로 두고 **그리는 순간** `storyNoticeCaptionPlacement` 가 자리를 정한다(9:16 렌더러 `StoryTemplate` 과
 *   패널 겹침 판정 `readStoryNoticeCaptionFit` 이 같은 함수를 부른다). 새로 까는 고지도 같은 `y` 를 싣는다 —
 *   두 값이 섞이면 «어느 작업물이 옛 자리냐»는 답할 수 없는 축이 생긴다.
 *
 * ★크기★ `sizePct = 0.0225` → 14.4px. 스토리 레이아웃의 **최소** 자막 크기(실측 15px)보다 작다 — 그
 *   최소값은 상수로 적지 않고 테스트가 `STORY_STYLE_LIST` 전수에서 실산출해 문다.
 * ★배경★ `bg:"box"` + 반투명 검정 → 밝은 이미지 위에서도 읽힌다(그림자·외곽선은 자막의 룩이라 안 쓴다).
 */
export const NOTICE_CAPTION_SPEC = {
  x: 0.5,
  y: 0.945,
  // ★0.026 → 0.0225 (적대 리뷰 P3-③ 실측)★ — 종전 값 16.64px 은 "자막보다 작다"는 주장과 달리
  //   **자막이 15px 인 스타일(썰 계열 다수)보다 컸다**. 리터럴 17 로 비교하던 테스트가 그걸 못 잡았다.
  //   14.4px 로 낮춰 전 스타일 최소 자막(15px)보다 확실히 작게 만든다(테스트가 전수로 문다).
  sizePct: 0.0225,
  weight: 600,
  align: "center",
  bg: "box",
  bgColor: "rgba(0,0,0,0.62)",
  color: "#EDEDED",
  shadow: false,
  outline: false,
} as const;

/**
 * 📐 렌더러 룩 상수 미러 — `TextOverlayLabelNode`(remotion)의 박스 기하.
 * ★여기서 다시 적는 이유★: 규격이 프레임 안에 드는지 **순수 계산으로 검증**하려면 그 기하가 필요한데,
 *   렌더 노드는 React 컴포넌트라 순수부만 떼어낼 수 없다. 값이 갈리면 테스트가 잡도록 한 곳에 모은다.
 *
 * ★1.2 는 근사가 아니라 **계약**이다(적대 리뷰 P3-②)★ — 종전엔 "브라우저 `line-height: normal` 근사"
 *   라고 적어 두고 렌더 노드는 아무 값도 지정하지 않았다. `normal` 은 글꼴이 정하는 값이라(Noto Sans KR
 *   은 1.36 근처) 미러와 실렌더가 갈렸다. 이제 **고지 경로만** 이 값을 명시로 받는다
 *   (`TextOverlayLabelNode` 의 `lineHeightEm` prop) — 기존 자유 오버레이·커버 라벨의 룩은 1픽셀도
 *   안 건드리고(그쪽은 여전히 `normal`), 고지 박스 높이만 계산과 렌더가 같은 수를 쓴다.
 */
export const NOTICE_CAPTION_LINE_HEIGHT_EM = 1.2;
const BOX_LINE_HEIGHT_EM = NOTICE_CAPTION_LINE_HEIGHT_EM;
const BOX_PAD_Y_EM = 0.28;       // TextOverlayLabelNode: padding `${fs*0.28}px ${fs*0.45}px`
const BOX_PAD_X_EM = 0.45;
/** 좌우 안전 여백(px, 360 기준) — 프레임 가장자리에 붙지 않게 두는 몫. */
const SIDE_SAFE_PX = 12;

/** 고지 글자 크기(px, 640 기준). */
export function noticeCaptionFontPx(frameH = NOTICE_CAPTION_FRAME_H): number {
  return NOTICE_CAPTION_SPEC.sizePct * frameH;
}

/** 고지 박스 높이(px, 640 기준) — 글자 + 상하 패딩. */
export function noticeCaptionBoxHeightPx(frameH = NOTICE_CAPTION_FRAME_H): number {
  const fs = noticeCaptionFontPx(frameH);
  return fs * (BOX_LINE_HEIGHT_EM + BOX_PAD_Y_EM * 2);
}

/** 고지 박스와 이웃(자막 블록·사진 카드 가장자리) 사이 여백(px, 640 기준). */
export const NOTICE_CAPTION_GAP_PX = 4;

/**
 * 📐 고지가 설 수 있는 세로 범위(px, 640 기준) = 9:16 세이프존 **core**(`safe-zone.ts` 실측 21.09~73.18%).
 * 고지는 «읽어야 하는 글자»라 빗금(제목·로고용) 구역에도 두지 않는다.
 */
export function noticeCaptionCoreRangePx(frameH = NOTICE_CAPTION_FRAME_H): { top: number; bottom: number } {
  const core = SAFE_ZONE["9:16"].core;
  return { top: core.top * frameH, bottom: (1 - core.bottom) * frameH };
}

const finiteOr = (v: unknown, fallback: number): number =>
  typeof v === "number" && Number.isFinite(v) ? v : fallback;

/**
 * 📏 **자막 블록 윗변·바닥(px, 640 기준)** — `StoryTemplate` 본문 자막 `top` 과 **같은 함수**다(그쪽이 이걸 부른다).
 *   · 씨네(`theme === "cinematic"`)는 세이프존 접기(`foldCineStoryCaptionBody`)를 거친 윗변 — 저장된 `startY` 가 아니다.
 *   · 카드형은 `startY` 그대로(카드 위 본문 자리라 접지 않는다 — 접기 함수 주석).
 *   · 바닥 = 윗변 + 한 화면 줄 수 × 줄 간격(줄마다 `minHeight = lineGap`).
 *
 * @param rows 그 작업물의 한 화면 자막 줄 수(= `storySubtitlePageCap` 결과 · 유저 지정 1~4 포함)
 * @param titleExtra 제목 줄바꿈으로 아래 전부가 내려간 양(`storyTitleExtra`) — 씨네는 0
 */
/** 고지가 어디에 기대 섰는가 — 테스트·측정이 «왜 거기냐»를 읽는 재료. */
export type NoticeCaptionAnchor = "above-caption" | "card-top" | "core-bottom";

export type StoryNoticeCaptionPlacement = {
  anchor: NoticeCaptionAnchor;
  /** 박스 중심·윗변·바닥(px, 640 기준). */
  centerPx: number;
  topPx: number;
  bottomPx: number;
};

/**
 * 📍 **고지를 그릴 자리**(9:16 · 회장님 2026-09-24 결정 «자막 바로 위, 안쪽» + 카드형 1번).
 *
 *   ① 자막이 사진 **아래**(씨네 풀블리드·자막 아래형) → 자막 블록 **바로 위**.
 *   ② 자막이 사진 **위**(카드형 — 글이 위, 사진 카드가 아래) → 사진 카드 **윗부분 안쪽**(= 자막 바로 아래).
 *      유저가 「한 화면 자막 줄 수」를 카드 고정 줄 수보다 올리면 자막 블록이 카드 위로 내려온다(카드는 고정 자리 그대로 ·
 *      #3174) — 그때는 **자막 블록 바닥 아래**에 선다(395종 × 캡 4 실측: 카드 윗변 기준이면 45종 겹침 → 0).
 *      글자 그대로 «자막 위»에 두면 제목 아래 메타 줄(조회수·작성자)과 겹친다(395종 중 약 50종 · 최대 15px 실측).
 *      사진 위에 붙어야 «무엇에 대한 고지인지»가 보인다.
 *   ③ 본문 자막이 안 그려지는 화면(통화 레인·표가 본문 대신) → core 아래 끝 바로 안.
 *   어느 갈래든 마지막에 **core 안으로 민다**(`noticeCaptionCoreRangePx`).
 *
 * 카드가 «쓸 수 있다» = 윗변이 자막 윗변 아래 · 고지 박스가 들어갈 높이 · 박스가 core 안에 선다.
 *   사진 없는 글 전용 스타일(카드 높이 1px · 윗변 640)은 여기서 걸러져 ①로 간다.
 * ★입력은 **그 프레임의 실제 값**★ — 카드 윗변은 줄 수에 따라 내려가므로(`storyCardTopY`) 렌더는 매 프레임
 *   같은 값을 넣고, 패널 판정은 한 화면 최대 줄 수 기준 값을 넣는다.
 */
export function storyNoticeCaptionPlacement(input: {
  /** 자막 블록 윗변(px) — 본문 자막이 안 그려지면 `null`. */
  captionTopPx: number | null;
  /** 자막 블록 바닥(px) — 한 화면 최대 줄 수 기준(`storyCaptionBlockPx`). 줄이 차고 빠져도 고지가 안 흔들린다. */
  captionBottomPx: number | null;
  /** 사진 카드 윗변(px). */
  cardTopPx: number | null;
  /** 사진 카드 높이(px). */
  cardHeightPx: number | null;
}): StoryNoticeCaptionPlacement {
  const boxH = noticeCaptionBoxHeightPx();
  const gap = NOTICE_CAPTION_GAP_PX;
  const core = noticeCaptionCoreRangePx();
  const captionTop = typeof input.captionTopPx === "number" && Number.isFinite(input.captionTopPx) ? input.captionTopPx : null;
  const cardTop = finiteOr(input.cardTopPx, Number.NaN);
  const cardH = finiteOr(input.cardHeightPx, Number.NaN);
  const cardUsable = captionTop != null
    && Number.isFinite(cardTop) && Number.isFinite(cardH)
    && cardTop >= captionTop
    && cardH >= boxH + gap * 2
    && cardTop + gap + boxH <= core.bottom;
  let anchor: NoticeCaptionAnchor;
  let top: number;
  if (captionTop != null && cardUsable) {
    anchor = "card-top";
    const captionBottom = finiteOr(input.captionBottomPx, captionTop);
    top = Math.max(cardTop, captionBottom) + gap;
  } else if (captionTop != null) {
    anchor = "above-caption";
    top = captionTop - gap - boxH;
  } else {
    anchor = "core-bottom";
    top = core.bottom - boxH;
  }
  top = Math.min(Math.max(top, core.top), core.bottom - boxH);
  return { anchor, topPx: top, bottomPx: top + boxH, centerPx: top + boxH / 2 };
}

/**
 * 📏 **한 줄 폭 예산(em)** — 이 값을 넘으면 말줄임한다.
 *
 * ★유도값이 낡아 있었다(적대 리뷰 P3-①)★ — 종전 주석은 글자 **16.64px** 기준으로 19.3em 을 유도해
 *   18em 을 잡았는데, 규격 크기는 그 뒤 **14.4px**(sizePct 0.0225)로 내려갔다. 글자가 작아지면 같은
 *   폭에 더 많이 들어가므로 예산도 함께 올라갔어야 하는데 18em 이 그대로 남아, **들어가는 문장을
 *   잘라 놓고 있었다**(고지는 법적 문구라 잘림 자체가 손해다).
 *
 * 재유도(360×640 · 실측): 쓸 수 있는 폭 = 360 − 좌우 안전여백(12×2) − 박스 좌우 패딩
 *   (0.45em×2 = 14.4×0.9 = 12.96px) = 323.04px. 글자 14.4px 이므로 323.04 / 14.4 ≈ **22.43em**
 *   (= `noticeCaptionUsableWidthEm()`). 폰트 실측 편차(자간·글꼴 대체)를 ~6% 남겨 **21em**.
 * ★예산은 유도값을 넘지 않는다★ — 넘으면 잘라 놓고도 프레임을 벗어나므로 말줄임이 무의미해진다
 *   (테스트가 `<= noticeCaptionUsableWidthEm()` 로 문다).
 */
export const NOTICE_CAPTION_WIDTH_BUDGET_EM = 21;

/** 폭 예산의 유도값(테스트가 예산이 기하를 넘지 않는지 확인한다). */
export function noticeCaptionUsableWidthEm(frameW = NOTICE_CAPTION_FRAME_W, frameH = NOTICE_CAPTION_FRAME_H): number {
  const fs = noticeCaptionFontPx(frameH);
  return (frameW - SIDE_SAFE_PX * 2 - fs * BOX_PAD_X_EM * 2) / fs;
}

/** 말줄임 기호(U+2026) — **전각 취급**. 아래 `charWidthEm` 이 그 폭을 실제로 1.0 으로 내는지는
 *  테스트가 못 박는다(적대 리뷰 P3-①: 주석은 전각이라 적고 산정은 0.55 를 빼고 있었다). */
const ELLIPSIS = "…";

/**
 * 글자 하나의 폭(em) 근사 — 한글·한자·가나·전각기호는 1.0, 공백은 0.30, 그 밖(라틴·숫자·문장부호)은 0.55.
 * ★근사인 것을 감춘 척하지 않는다★ — 실측 폭은 글꼴에 따라 갈린다. 그래서 예산에 여유(19.3 → 18)를 뒀다.
 */
function charWidthEm(ch: string): number {
  if (ch === " ") return 0.3;
  const cp = ch.codePointAt(0) ?? 0;
  // ⋯ U+2026(…) — 한국어 조판에서 전각으로 앉는다. 아래 CJK 구간(0x2e80~)에 안 들어가므로 명시한다.
  //   말줄임 예산이 이 폭을 빼고 잘리므로, 여기가 실폭보다 좁으면 잘라 놓고도 예산을 넘는다.
  if (cp === 0x2026) return 1;
  // 한글(자모·완성형)·CJK 통합한자·가나·전각 폼 — 전각 취급.
  const wide = (cp >= 0x1100 && cp <= 0x115f)
    || (cp >= 0x2e80 && cp <= 0xa4cf)
    || (cp >= 0xac00 && cp <= 0xd7a3)
    || (cp >= 0xf900 && cp <= 0xfaff)
    || (cp >= 0xfe30 && cp <= 0xfe6f)
    || (cp >= 0xff00 && cp <= 0xff60)
    || (cp >= 0xffe0 && cp <= 0xffe6);
  return wide ? 1 : 0.55;
}

/** 문구의 폭(em) — 순수 함수(테스트·UI 잔여 표시 공용). */
export function noticeCaptionWidthEm(text: string): number {
  let w = 0;
  for (const ch of text) w += charWidthEm(ch);
  return w;
}

/**
 * 🧹 **고지 문구 정규화 + 말줄임** — 순수 함수.
 *
 * 정책(그리고 그 정책인 이유):
 *   ① **한 줄로 접는다**(개행·탭·연속 공백 → 공백 1개). 고지는 "하단 한 줄"이 규격이고, 두 줄이 되면
 *      박스가 자막 영역을 침범한다.
 *   ② **자동 줄바꿈을 쓰지 않는다.** 소비 노드(`CoverLabelNode`)는 `fitToFrame` 을 켜지 않고,
 *      캔버스 렌더러(`drawTextOverlays`)는 `\n` 분할만 알 뿐 자동 접기가 없다 — 여기서 접으면
 *      **두 렌더러의 그림이 갈린다**. 그래서 접는 대신 **자른다**.
 *   ③ 폭 예산을 넘으면 말줄임표(…)를 포함해 예산 안으로 자른다.
 */
export function normalizeNoticeCaptionText(raw: unknown): string {
  if (typeof raw !== "string") return "";
  const flat = raw.replace(/\s+/g, " ").trim();
  if (!flat) return "";
  if (noticeCaptionWidthEm(flat) <= NOTICE_CAPTION_WIDTH_BUDGET_EM) return flat;
  const budget = NOTICE_CAPTION_WIDTH_BUDGET_EM - charWidthEm(ELLIPSIS);
  let w = 0;
  let out = "";
  for (const ch of flat) {
    const next = w + charWidthEm(ch);
    if (next > budget) break;
    out += ch;
    w = next;
  }
  return `${out.trimEnd()}${ELLIPSIS}`;
}

/** 이 오버레이가 고지인가 — `id` 접두로만 판정한다(문구 대조 금지: 자유 입력이 프리셋과 같을 수 있다). */
export function isNoticeCaptionOverlay(o: unknown): boolean {
  if (!o || typeof o !== "object") return false;
  const id = (o as { id?: unknown }).id;
  return typeof id === "string" && id.startsWith(NOTICE_CAPTION_ID_PREFIX);
}

/** 이 고지 오버레이의 출처 — 접두 뒤 갈래. 알 수 없으면 "all"(=덮어도 되는 쪽)로 본다. */
export function noticeCaptionSourceOf(o: unknown): NoticeCaptionSource | null {
  if (!isNoticeCaptionOverlay(o)) return null;
  const id = (o as { id: string }).id;
  if (id === NOTICE_CAPTION_ID.one) return "one";
  if (id === NOTICE_CAPTION_ID.off) return "off";
  return "all";
}

/**
 * 규격대로 조립한 고지 오버레이 — 문구가 비면 `null`(= 고지 없음).
 * ★`start`·`end` 를 싣지 않는다★ — 없으면 씬 창 전체다(CoverLabelNode 판정). 고지는 씬이 보이는
 *   내내 떠 있어야 의미가 있고, 창을 주면 "언제부터 언제까지 고지였나"라는 답할 수 없는 축이 생긴다.
 */
export function buildNoticeCaptionOverlay(text: unknown, source: NoticeCaptionSource): MbTextOverlay | null {
  const t = normalizeNoticeCaptionText(text);
  if (!t) return null;
  return { ...NOTICE_CAPTION_SPEC, text: t, id: NOTICE_CAPTION_ID[source] };
}

/**
 * 🪦 **개별 해제 표식**(빈 문구 무덤) — "이 컷은 유저가 직접 껐다"를 데이터에 남긴다.
 * 문구가 비어 있어 **두 렌더러 모두 아무것도 그리지 않는다**(화면 영향 0). 존재 이유는 오직
 * 「일괄 적용이 이 컷을 되살리지 않게」다(`NoticeCaptionSource` 의 "off" 주석 참조).
 */
export function buildNoticeCaptionOptOut(): MbTextOverlay {
  return { ...NOTICE_CAPTION_SPEC, text: "", id: NOTICE_CAPTION_ID.off };
}
